import { useState } from 'react';
import { useRazorpay } from 'react-razorpay';
import axios from 'axios';
import { Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentButton = ({ 
  packageId, 
  packageName, 
  amount,
  className = '',
  onSuccess,
  onError 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const { Razorpay } = useRazorpay();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      // Create order on backend
      const orderResponse = await axios.post(`${API_URL}/api/payments/create-order`, {
        package_id: packageId,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone
      });

      const { order_id, amount: orderAmount, currency, key_id } = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: orderAmount,
        currency: currency,
        name: 'Kirit Sobti Advisory',
        description: packageName,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            await axios.post(`${API_URL}/api/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            toast.success('Payment successful! We will contact you shortly.');
            setShowForm(false);
            setFormData({ name: '', email: '', phone: '' });
            onSuccess?.(response);
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
            onError?.(error);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#D4AF37'
        },
        modal: {
          ondismiss: () => {
            setIsLoading(false);
          }
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        onError?.(response.error);
      });
      razorpayInstance.open();
      
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to initiate payment. Please try again.');
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className={`btn-primary flex items-center justify-center gap-2 ${className}`}
        data-testid={`pay-btn-${packageId}`}
      >
        <CreditCard size={18} />
        Pay {amount}
      </button>
    );
  }

  return (
    <div className="space-y-3" data-testid={`payment-form-${packageId}`}>
      <input
        type="text"
        name="name"
        placeholder="Your Name"
        value={formData.name}
        onChange={handleInputChange}
        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#D4AF37]"
        data-testid="payment-name-input"
      />
      <input
        type="email"
        name="email"
        placeholder="Email Address"
        value={formData.email}
        onChange={handleInputChange}
        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#D4AF37]"
        data-testid="payment-email-input"
      />
      <input
        type="tel"
        name="phone"
        placeholder="Phone Number"
        value={formData.phone}
        onChange={handleInputChange}
        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#D4AF37]"
        data-testid="payment-phone-input"
      />
      <div className="flex gap-2">
        <button
          onClick={() => {
            setShowForm(false);
            setFormData({ name: '', email: '', phone: '' });
          }}
          className="flex-1 py-2 text-sm text-white/60 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          data-testid="payment-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handlePayment}
          disabled={isLoading}
          className={`flex-1 py-2 text-sm bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
          data-testid="payment-proceed-btn"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard size={16} />
              Pay Now
            </>
          )}
        </button>
      </div>
    </div>
  );
};
